import React, { useCallback, useState, type CSSProperties } from 'react';

const IMG = (name: string) => `/images/home-hero/${name}`;

/** Prefer .jpg (correct MIME for JPEG exports); .png kept as fallback. */
const NASTALIQ_POEM_SOURCES = [IMG('nastaliq-poem.jpg'), IMG('nastaliq-poem.png')];

/** Soft fade on all four edges (incl. left/right) with a gentle center vignette. */
const POEM_FADE_MASK: CSSProperties = {
  maskImage: [
    'radial-gradient(ellipse 25% 50% at 50% 50%, black 0%, black 28%, transparent 52%)',
    //'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
    //'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)'
  ].join(', '),
  WebkitMaskImage: [
    'radial-gradient(ellipse 25% 50% at 50% 50%, black 0%, black 28%, transparent 52%)',
    //'linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)',
    //'linear-gradient(to bottom, transparent 0%, black 8%, black 92%, transparent 100%)'
  ].join(', '),
  maskComposite: 'intersect',
  WebkitMaskComposite: 'source-in'
};

function DecorImage({ src, className }: { src: string; className: string }) {
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      className={className}
      loading="lazy"
      decoding="async"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = 'none';
      }}
    />
  );
}

function NastaliqPoemImage({ className }: { className: string }) {
  const [sourceIndex, setSourceIndex] = useState(0);
  const src = NASTALIQ_POEM_SOURCES[sourceIndex] ?? NASTALIQ_POEM_SOURCES[0];

  const onError = useCallback(() => {
    setSourceIndex((prev) => (prev < NASTALIQ_POEM_SOURCES.length - 1 ? prev + 1 : prev));
  }, []);

  if (sourceIndex >= NASTALIQ_POEM_SOURCES.length) return null;

  return (
    <img
      key={src}
      src={src}
      alt=""
      aria-hidden
      className={className}
      loading="eager"
      decoding="async"
      onError={onError}
    />
  );
}

export type NastaliqWatermarkVariant = 'hero' | 'auth' | 'header' | 'header-simple';

const NASTALIQ_WRAPPER_CLASS: Record<NastaliqWatermarkVariant, string> = {
  hero:
    'pointer-events-none absolute left-1/2 top-0 z-0 origin-center -translate-x-1/2 -translate-y-[70%] scale-[3.38] overflow-visible md:scale-[2.6]',
  /** Centered behind the auth logo — smaller scale, no upward bleed into the nav bar. */
  auth:
    'pointer-events-none absolute left-1/2 top-1/2 z-0 origin-center -translate-x-1/2 -translate-y-[28%] scale-[1.15] overflow-visible max-md:-translate-y-[52%] max-md:scale-[3.47] md:-translate-y-[30%] md:scale-[1.69]',
  header:
    'pointer-events-none absolute left-1/2 top-1/2 z-0 origin-center -translate-x-1/2 -translate-y-1/2 scale-[2.15] overflow-visible nastaliq-header-fade md:scale-[1.69]',
  'header-simple':
    'pointer-events-none absolute left-1/2 top-1/2 z-0 origin-center -translate-x-1/2 -translate-y-1/2 scale-[2.15] overflow-visible nastaliq-header-fade md:scale-[1.69]'
};

const NASTALIQ_IMAGE_CLASS: Record<NastaliqWatermarkVariant, string> = {
  hero: 'block h-auto w-[min(2024px,calc(98vw*1.1))] max-w-[calc(98vw*1.1)] max-h-[min(280px,40vw)] object-contain opacity-[0.09]',
  auth: 'block h-auto w-[min(2024px,calc(98vw*1.1))] max-w-[calc(98vw*1.1)] max-h-[min(280px,40vw)] object-contain opacity-[0.08]',
  header:
    'block h-auto w-[min(2024px,calc(98vw*1.1))] max-w-[calc(98vw*1.1)] max-h-[min(280px,40vw)] object-contain opacity-[0.055]',
  'header-simple':
    'block h-auto w-[min(2024px,calc(98vw*1.1))] max-w-[calc(98vw*1.1)] max-h-[min(280px,40vw)] object-contain opacity-[0.055]'
};

/**
 * Nastaliq watermark — absolutely positioned so it does not affect heading spacing.
 * Use `variant="auth"` on sign-in / sign-up (behind the brand logo).
 */
export const HomeHeroNastaliqPoem: React.FC<{ variant?: NastaliqWatermarkVariant }> = ({
  variant = 'hero'
}) => (
  <div className={NASTALIQ_WRAPPER_CLASS[variant]} style={POEM_FADE_MASK} aria-hidden>
    <NastaliqPoemImage className={NASTALIQ_IMAGE_CLASS[variant]} />
  </div>
);

/** Full-bleed background decor for the marketing hero. */
export const HomeHeroPersianDecor: React.FC = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
    <div
      className="absolute inset-x-0 top-0 h-[min(58vh,480px)] md:h-[min(72vh,620px)] lg:h-[min(78vh,700px)]"
      style={{
        maskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 45%, transparent 100%)'
      }}
    >
      <DecorImage
        src={IMG('eslimi-tilework.png')}
        className="h-full w-full object-cover object-top opacity-[0.19] blur-[0.5px] saturate-[1.15] dark:opacity-[0.24] dark:saturate-[0.95]"
      />
    </div>
  </div>
);

export default HomeHeroPersianDecor;
